import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, X, Filter, Users, MapPin, Tag, Maximize, Building2, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import AdvancedFiltersModal from './AdvancedFiltersModal';

interface ModernEventFiltersProps {
  selectedDate?: Date | null;
  onFiltersChange?: (filters: any) => void;
  events?: any[];
  filterType?: 'students' | 'categories' | 'locations' | 'schools' | 'teachers' | 'all';
}

const ModernEventFilters: React.FC<ModernEventFiltersProps> = ({
  selectedDate,
  onFiltersChange,
  events = [],
  filterType = 'all'
}) => {
  const { t } = useLanguage();
  const { token, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);


  const { data: students = [] } = useQuery({
    queryKey: ['students', token],
    queryFn: () => eventService.getStudents(null, token),
  });

  /*const { data: categories = [] } = useQuery({
    queryKey: ['categories', token],
    queryFn: () => eventService.getCategories(null, token),
    select: (data) => [
      { id: 0, name: t('agenda.event'), isFake: true },
      { id: -1, name: t('agenda.eventNotConfigured'), isFake: true },
      ...data,
    ],
  });*/

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', token],
    queryFn: () => eventService.getCategories(null, token),
    select: (data) => {
      const knownCategoryIds = new Set(data.map(cat => cat.id?.toString()));

      // Ici tu dois avoir accès à la liste des events (déjà fetchée)
      const teacherFakeCategories = [];

      if (user?.account_type === 'S' && Array.isArray(events)) {
        for (const event of events) {
          const categoryId = event.extendedProps?.categoryId?.toString();
          const categoryName = event.extendedProps?.category;
          const isTeacherInvoice = event.extendedProps?.invoice_type === 'T';

          if (isTeacherInvoice && categoryId && categoryName && !knownCategoryIds.has(categoryId) && categoryId !== "0") {
            teacherFakeCategories.push({
              id: parseInt(categoryId?.toString()),
              name: categoryName,
              isFake: true,
              isTeacherCat:true,
              color: '#888888'
            });
            knownCategoryIds.add(categoryId); // éviter les doublons
          }
        }
      }

      return [
        { id: 0, name: t('agenda.event'), isFake: true, isTeacherCat: false },
        { id: -1, name: t('agenda.eventNotConfigured'), isFake: true, isTeacherCat:false },
        ...data,
        ...teacherFakeCategories,
      ];
    },
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations', token],
    queryFn: () => eventService.getLocations(null, token),
  });

  const { data: teachers = [] } = useQuery({
    queryKey: ['teachers', token],
    queryFn: () => eventService.getTeachers(null, token),
  });

  const filteredEvents = useMemo(() => {
    return events.filter(event => event.type === 10 || event.type === 100 || event.type === 50 || event.type === 51);
  }, [events]);

  const studentsInEvents = useMemo(() => {
    const studentIds = new Set<string>();
    filteredEvents.forEach(event => {
      if (event.extendedProps?.studentIds) {
        event.extendedProps.studentIds.forEach((id: string) => {
          studentIds.add(id.toString()); // Assurez-vous que l'ID est une chaîne de caractères
        });
      }
    });

    return students
      .filter(student => studentIds.has(student.id.toString())) // Comparez avec une chaîne de caractères
      .sort((a, b) => a.id.toString().localeCompare(b.id.toString())); // Triez par chaîne de caractères
  }, [filteredEvents, students]);


  const categoriesInEvents = useMemo(() => {
    const categoryIds = new Set<string>();
    filteredEvents.forEach(event => {
      const id = event.extendedProps?.categoryId ?? 0;
      categoryIds.add(id.toString());
    });
    return categories.filter(category => categoryIds.has(category.id.toString()));
  }, [filteredEvents, categories]);



  const locationsInEvents = useMemo(() => {
  const locationIds = new Set<string>();
  events.forEach(event => {
    if (event.extendedProps?.locationId) {
      locationIds.add(event.extendedProps.locationId.toString());
    }
  });
  return locations.filter(location => locationIds.has(location.id.toString()));
}, [events, locations]);

  const schoolsInEvents = useMemo(() => {
    if (!user.schools) {
      return [];
    }
    const schoolIds = new Set<string>();
    filteredEvents.forEach(event => {
      if (event.extendedProps?.laschool) {
        schoolIds.add(event.extendedProps.laschool.toString());
      }
    });

    return user.schools
      .filter(school => schoolIds.has(school.id.toString()))
      .sort((a, b) => a.id.toString().localeCompare(b.id.toString()));
  }, [filteredEvents, user]);

  const teachersInEvents = useMemo(() => {
    const teacherIds = new Set<string>();
    filteredEvents.forEach(event => {
      if (event.extendedProps?.teacherId) {
        teacherIds.add(event.extendedProps.teacherId.toString());
      }
    });
    return teachers.filter(teacher => teacherIds.has(teacher.id.toString()));
  }, [filteredEvents, teachers]);

  const [activeFilters, setActiveFilters] = useState({
    students: [] as string[],
    categories: [] as string[],
    locations: [] as string[],
    schools: [] as string[],
    teachers: [] as string[],
    showCompleted: true
  });



  const toggleFilter = useCallback((type, id) => {
    setActiveFilters(prev => {
      const current = prev[type];
      const isActive = current.includes(id);
      const newFilters = {
        ...prev,
        [type]: isActive ? current.filter(item => item !== id) : [...current, id]
      };
      // Assurez-vous que onFiltersChange ne rappelle pas l'API
      onFiltersChange?.(newFilters);
      return newFilters;
    });
  }, [onFiltersChange]);


  const clearFilters = useCallback((type: 'students' | 'categories' | 'locations' | 'schools' | 'teachers') => {
    setActiveFilters(prev => {
      const newFilters = { ...prev, [type]: [] };
      onFiltersChange?.(newFilters);
      return newFilters;
    });
  }, [onFiltersChange]);

  const selectAllFilters = useCallback((type: 'students' | 'categories' | 'locations' | 'schools' | 'teachers') => {
  let ids: string[] = [];
  if (type === 'students') {
    ids = studentsInEvents.map(s => s.id.toString()); // Assurez-vous que les IDs sont des chaînes de caractères
  } else if (type === 'categories') {
    ids = categoriesInEvents.map(c => c.id.toString());
  } else if (type === 'locations') {
    ids = locationsInEvents.map(l => l.id.toString());
  } else if (type === 'schools') {
    ids = schoolsInEvents.map(s => s.id.toString());
  } else if (type === 'teachers') {
    ids = teachersInEvents.map(t => t.id.toString());
  }
  setActiveFilters(prev => {
    const newFilters = { ...prev, [type]: ids };
    onFiltersChange?.(newFilters);
    return newFilters;
  });
}, [studentsInEvents, categoriesInEvents, locationsInEvents, schoolsInEvents, teachersInEvents, onFiltersChange]);

  const handleShowCompletedChange = useCallback((checked: boolean) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev, showCompleted: checked };
      onFiltersChange?.(newFilters);
      return newFilters;
    });
  }, [onFiltersChange]);

  const renderFilterItem = (item: any, type: 'students' | 'categories' | 'locations' | 'schools' | 'teachers') => {
    const isActive = activeFilters[type].includes(item.id.toString());
    let name;
    if (type === 'students' || type === 'teachers') {
      name = item.name || `${item.firstname} ${item.lastname}`;
    } else if (type === 'schools') {
      name = item.school_name;
    } else {
      name = item.name;
    }
    
    return (
      <div
        key={item.id}
        onClick={() => toggleFilter(type, item.id.toString())}
        className={`
        grid grid-cols-[auto_1fr_auto] items-center gap-2 p-2 rounded-md cursor-pointer 
        ${isActive ? 'bg-primary/10 border-primary/30 border' : 'hover:bg-gray-50 border border-transparent'}
        mb-1 w-full overflow-hidden
      `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {type === 'categories' && (
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.color || '#888888' }}
            />
          )}
          {type === 'students' && <Users className="h-3 w-3 text-gray-500 flex-shrink-0" />}
          {type === 'schools' && item.school_type === 'S' && <Building2 className="h-3 w-3 text-gray-500 flex-shrink-0" />}
          {type === 'schools' && item.school_type === 'C' && <GraduationCap className="h-3 w-3 text-gray-500 flex-shrink-0" />}
          {type === 'teachers' && <GraduationCap className="h-3 w-3 text-gray-500 flex-shrink-0" />}
          {type === 'locations' && <MapPin className="h-3 w-3 text-gray-500 flex-shrink-0" />}
          <span className="text-sm truncate min-w-0 w-full">{name}</span>
        </div>
        {isActive && (
          <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-white" />
          </div>
        )}
      </div>
    );
  };

  const searchResults = useMemo(() => {
    if (!searchQuery) return { students: [], categories: [], locations: [], schools: [], teachers: [] };
    const query = searchQuery.toLowerCase();
    return {
      students: studentsInEvents.filter(student => {
        const name = student.name || `${student.firstname || ''} ${student.lastname || ''}`;
        return name.toLowerCase().includes(query);
      }),
      categories: categoriesInEvents.filter(category =>
        category.name.toLowerCase().includes(query)
      ),
      locations: locationsInEvents.filter(location =>
        location.name.toLowerCase().includes(query)
      ),
      schools: schoolsInEvents.filter(school =>
        school.school_name.toLowerCase().includes(query)
      ),
      teachers: teachersInEvents.filter(teacher => {
        const name = teacher.name || `${teacher.firstname || ''} ${teacher.lastname || ''}`;
        return name.toLowerCase().includes(query);
      })
    };
  }, [searchQuery, studentsInEvents, categoriesInEvents, locationsInEvents, schoolsInEvents, teachersInEvents]);

  return (
    <div className="space-y-4 p-1">
      <div className="space-y-3">
        {/* Titre "Filtres" + icône recherche/croix */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <h3 className="text-sm font-medium">{t('agenda.filters')}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (isSearchMode) {
                  setSearchQuery('');
                  setIsSearchMode(false);
                } else {
                  setIsSearchMode(true);
                }
              }}
              className="h-6 w-6 p-0"
            >
              {isSearchMode ? <X className="h-3 w-3" /> : <Search className="h-3 w-3" />}
            </Button>
          </div>
        </div>

        {/* Champ de recherche visible uniquement si mode recherche activé */}
        {isSearchMode && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t('agenda.searchFilters') || 'Rechercher...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Switch "Afficher les événements terminés" */}
        {!isSearchMode && (
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <Switch
              id="show-completed"
              checked={activeFilters.showCompleted}
              onCheckedChange={handleShowCompletedChange}
            />
            <Label htmlFor="show-completed" className="text-sm">
              {t('agenda.showCompletedEvents')}
            </Label>
          </div>
        )}
      </div>
      {/* Affichage des catégories */}
      {(filterType === 'all' || filterType === 'categories') && categoriesInEvents.length > 0 && !isSearchMode && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-xs font-semibold flex items-center gap-1">
              <Tag className="w-3 h-3" />
              {t('agenda.categories')} ({activeFilters.categories.length}/{categoriesInEvents.length})
            </h4>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllFilters('categories')}
                className="h-6 px-2 text-xs"
              >
                {t('agenda.selectAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearFilters('categories')}
                className="h-6 px-2 text-xs"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
          <ScrollArea className="h-auto">
            {categoriesInEvents.map((category) => renderFilterItem(category, 'categories'))}
          </ScrollArea>
          <Separator className="my-3" />
        </div>
      )}

      {/* Affichage des écoles */}
      {(filterType === 'all' || filterType === 'schools') && schoolsInEvents.length > 0 && !isSearchMode && user?.account_type !== 'S' &&  (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-xs font-semibold flex items-center gap-1">
              <Users className="w-3 h-3" />
              {t('agenda.schools')} ({activeFilters.schools.length}/{schoolsInEvents.length})
            </h4>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllFilters('schools')}
                className="h-6 px-2 text-xs"
              >
                {t('agenda.selectAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearFilters('schools')}
                className="h-6 px-2 text-xs"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
          <ScrollArea className="h-auto">
            {(searchQuery ? searchResults.schools : schoolsInEvents).map((school) => renderFilterItem(school, 'schools'))}
          </ScrollArea>
          <Separator className="my-3" />
        </div>
      )}

      {/* Affichage des profs */}
      {(filterType === 'all' || filterType === 'teachers') && teachersInEvents.length > 0 && !isSearchMode && user?.account_type !== 'C' && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-xs font-semibold flex items-center gap-1">
              <GraduationCap className="w-3 h-3" />
              {t('agenda.teachers')} ({activeFilters.teachers.length}/{teachersInEvents.length})
            </h4>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllFilters('teachers')}
                className="h-6 px-2 text-xs"
              >
                {t('agenda.selectAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearFilters('teachers')}
                className="h-6 px-2 text-xs"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
          <ScrollArea className="h-auto">
            {(searchQuery ? searchResults.teachers : teachersInEvents).map((teacher) => renderFilterItem(teacher, 'teachers'))}
          </ScrollArea>
          <Separator className="my-3" />
        </div>
      )}


      {(filterType === 'all' || filterType === 'students') && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-xs font-semibold flex items-center gap-1">
              <Users className="w-3 h-3" />
              {t('agenda.students')} ({activeFilters.students.length}/{studentsInEvents.length})
            </h4>
            <div className="flex gap-1">
              {!isSearchMode && (
                <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllFilters('students')}
                className="h-6 px-2 text-xs"
              >
                {t('agenda.selectAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearFilters('students')}
                className="h-6 px-2 text-xs"
              >
                <X size={16} />
              </Button>
              </>
              )}
            </div>
          </div>
          <ScrollArea className="h-auto">
            {(searchQuery ? searchResults.students : studentsInEvents).map((student) =>
              renderFilterItem(student, 'students')
            )}
          </ScrollArea>
          <Separator className="my-3" />
        </div>
      )}


      {(filterType === 'all' || filterType === 'locations') && locationsInEvents.length > 0 && !isSearchMode && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-xs font-semibold flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {t('agenda.locations')} ({activeFilters.locations.length}/{locationsInEvents.length})
            </h4>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllFilters('locations')}
                className="h-6 px-2 text-xs"
              >
                {t('agenda.selectAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearFilters('locations')}
                className="h-6 px-2 text-xs"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
          <ScrollArea className="max-h-20">
            {locationsInEvents.map((location) => renderFilterItem(location, 'locations'))}
          </ScrollArea>
        </div>
      )}

      <AdvancedFiltersModal
        open={showAdvancedModal}
        onOpenChange={setShowAdvancedModal}
        filters={activeFilters}
        onFiltersChange={(filters) => {
          setActiveFilters(filters);
          onFiltersChange?.(filters);
        }}
        students={studentsInEvents}
        categories={categoriesInEvents}
        locations={locationsInEvents}
        teachers={teachersInEvents}
      />
    </div>
  );
};

export default ModernEventFilters;
